import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { useLoggedInEmployeeId, useLoggedInUserRoles } from '~/components/Auth';
import { useApplicationConfig } from '~/data/applicationConfig';
import {
  fetchEmployeeLoginData,
  useEmployee,
  useEmployeeLoginData,
} from '~/data/employee';
import { Roles } from '~/data/user';
import { useSelectedEmployeeId } from '~/hooks/employee';
import { UseDialogStateOutput, useDialogState } from '~/hooks/useDialogState';
import { isRoleHRAdmin } from '~/services/Permissions';
import { AppDispatch } from '~/store';

type PropsType = {
  employeeId: number;
};

export type UseEmployeeActionOutput = {
  dataLoaded: boolean;
  user: {
    isInternal: boolean;
    isExternal: boolean;
  };
  permissions: {
    canChangeLoginMethod: boolean;
    canResetInternalPassword: boolean;
    canViewAccountManagementMenu: boolean;
    canChangeRoles: boolean;
    canViewUserActivity: boolean;
  };
  dialogs: {
    createUserAccountDialog: UseDialogStateOutput;
    changeRolesDialog: UseDialogStateOutput;
    changeLoginMethod: UseDialogStateOutput;
    resetPasswordDialog: UseDialogStateOutput;
    changeUserNameDialog: UseDialogStateOutput;
    changeAccountStatusDialog: UseDialogStateOutput;
    cropImageDialog: UseDialogStateOutput;
    activityTrackingDialog: UseDialogStateOutput;
  };
};

export const useEmployeeAction = (
  props: PropsType,
): UseEmployeeActionOutput => {
  const { employeeId } = props;

  const dispatch: AppDispatch = useDispatch();

  const {
    employee: { empBasicData },
  } = useEmployee();
  const {
    selectedEmployeeDetails: { hasRoleAccessOverEmployee },
  } = useSelectedEmployeeId();

  const { isExternal, isInternal } = useMemo(() => {
    const isInternalEmployee = !!empBasicData?.fEmpHasAccount;

    return {
      isExternal: !isInternalEmployee,
      isInternal: isInternalEmployee,
    };
  }, [empBasicData]);

  const { applicationConfig, hasData: applicationConfigLoaded } =
    useApplicationConfig();

  const {
    employeeAdminLoginData,
    loginDataLoading,
    loginDataFailedLoading,
    hasLoginData,
  } = useEmployeeLoginData();

  useEffect(() => {
    dispatch(fetchEmployeeLoginData({ empId: employeeId }));
  }, [dispatch, employeeId]);

  const configPermissions = useMemo(() => {
    const internalSignInEnabled =
      applicationConfig.fComConfig?.coreConfig?.fComInternalSignin || false;
    const externalSignInEnabled =
      applicationConfig.fComConfig?.coreConfig?.fComInternalSignin || false;
    const userLoginIsExternal =
      employeeAdminLoginData.fLoginIsExternal || false;

    // Access checks
    const canChangeLoginMethod =
      internalSignInEnabled && externalSignInEnabled && isInternal;
    const canResetInternalPassword =
      internalSignInEnabled &&
      (!externalSignInEnabled ||
        (externalSignInEnabled && !userLoginIsExternal)) &&
      isInternal;

    return {
      canChangeLoginMethod,
      canResetInternalPassword,
    };
  }, [applicationConfig, employeeAdminLoginData, isInternal]);

  const loggedInEmployeeId = useLoggedInEmployeeId();
  const roles = useLoggedInUserRoles();

  const rolesPermissions = useMemo(() => {
    return {
      canViewAccountManagementMenu: hasRoleAccessOverEmployee(
        [
          Roles.HR_ADMIN,
          Roles.HR_ADMIN_LIMITED,
          Roles.HR_STAFF,
          Roles.IT_ADMIN,
        ].filter((role) => roles.includes(role.toString())),
      ),
      canChangeRoles: !roles.includes(Roles.IT_ADMIN) && isInternal,
      canViewUserActivity:
        employeeId !== loggedInEmployeeId && isRoleHRAdmin(roles) && isInternal,
    };
  }, [
    roles,
    hasRoleAccessOverEmployee,
    employeeId,
    loggedInEmployeeId,
    isInternal,
  ]);

  const loginDataLoaded =
    (!loginDataLoading && !loginDataFailedLoading) || hasLoginData;
  const dataLoaded = isExternal
    ? applicationConfigLoaded
    : loginDataLoaded && applicationConfigLoaded;

  // Dialog states
  const createUserAccountDialog = useDialogState();
  const changeRolesDialog = useDialogState();
  const changeLoginMethod = useDialogState();
  const resetPasswordDialog = useDialogState();
  const changeUserNameDialog = useDialogState();
  const changeAccountStatusDialog = useDialogState();
  const cropImageDialog = useDialogState();
  const activityTrackingDialog = useDialogState();

  return {
    dataLoaded,
    user: {
      isExternal,
      isInternal,
    },
    permissions: {
      ...configPermissions,
      ...rolesPermissions,
    },
    dialogs: {
      createUserAccountDialog,
      changeRolesDialog,
      changeLoginMethod,
      resetPasswordDialog,
      changeUserNameDialog,
      changeAccountStatusDialog,
      cropImageDialog,
      activityTrackingDialog,
    },
  };
};
